/**
 * SDS SDK initialization module — ported from Designer
 * Responsible for SDK initialization, cleanup, and lifecycle management
 *
 * Key difference from Designer: workspace uses dot-path store keys
 * (e.g. 'design.sdkInstance') and unified event-bus.
 */

import { store } from '../../../core/store.js'
import { eventBus, EVENTS } from '../../../core/event-bus.js'
import { createLogger } from '../../../utils/logger.js'
import { SDS_CONFIG, fetchSdsSign } from './config.js'


const log = createLogger('sds-init')

function getPreviewSize() {
  const previewBox = document.getElementById('previewBox')
  const fallbackSize = 290

  if (!previewBox) {
    return fallbackSize
  }

  const w = previewBox.offsetWidth || previewBox.clientWidth
  const h = previewBox.offsetHeight || previewBox.clientHeight

  if (w === 0) {
    return fallbackSize
  }

  const measuredSize = Math.floor(Math.min(w, h || w))
  return Math.max(Math.min(measuredSize, 360), 180)
}

/**
 * Initialize SDS SDK
 * @param {string} productId - Product ID
 * @param {boolean} isSwitch - Whether this is a product switch
 */
export async function initDesigner(productId, isSwitch = false) {
  log.info('Initializing Designer for Product:', productId, isSwitch ? '(switch)' : '')

  store.setState('design.loading', true)
  // Only set product ID if no product data exists (preserve name/price/thumbnail from PRODUCT_SELECT)
  const existing = store.getState('design.currentProduct')
  if (!existing || String(existing.id) !== String(productId)) {
    store.setState('design.currentProduct', { id: productId })
  }

  showCanvasLoading(isSwitch ? 'Switching product...' : 'Loading canvas...')

  // Fetch SDK credentials from backend (if not already fetched)
  if (!SDS_CONFIG.appId || !SDS_CONFIG.sign) {
    log.info('Fetching SDK credentials from backend...')
    await fetchSdsSign()
  }

  // Clean up old instances
  await cleanup()

  // Check whether SDK is loaded
  const { SdsDesign } = window
  if (!SdsDesign) {
    throw new Error('SDS SDK not loaded')
  }

  // Prepare container
  await prepareContainers()

  // Create new SDK instance
  log.info('Creating new SDK instance...')
  const sdk = new SdsDesign()
  store.setState('design.sdkInstance', sdk)

  // Initialize SDK
  initializeSDK(sdk, productId)
}

/**
 * Show canvas load state
 */
function showCanvasLoading(text = 'Loading...') {
  const container = document.getElementById('designer-container')
  if (container) {
    // Reuse the polished .design-loading screen (brand spinner + ambiance) so the
    // canvas loading matches the rest, not a flat inline box. why → bao 2026-06-27.
    container.innerHTML = `
      <div class="design-loading">
        <div class="spinner"></div>
        <div class="design-loading-text">${text}</div>
      </div>
    `
  }
}

/**
 * Clean up old SDK instances
 */
export async function cleanup() {
  const sdk = store.getState('design.sdkInstance')

  if (sdk) {
    log.info('Cleaning up old SDK instance...')
    store.setState('design.isSDKReady', false)

    try {
      if (sdk.remove) {
        sdk.remove()
      }
      if (sdk.sarto && sdk.sarto.destroy) {
        sdk.sarto.destroy()
      }
    } catch (e) {
      log.warn('Error during SDK cleanup:', e)
    }

    store.setState('design.sdkInstance', null)
    await new Promise((resolve) => requestAnimationFrame(resolve))
  }
}

/**
 * Prepare canvas and preview container
 */
async function prepareContainers() {
  const designerContainer = document.getElementById('designer-container')
  const previewContainer = document.getElementById('previewBoxContent')

  if (!designerContainer) {
    throw new Error('Designer container not found')
  }

  // Per SDS docs: clear innerHTML, do NOT replace the DOM element
  log.info('Clearing canvas container (innerHTML)...')
  designerContainer.innerHTML = ''

  // Remove any inline styles from previous init — let CSS handle sizing
  designerContainer.removeAttribute('style')

  // Clear preview container
  if (previewContainer) {
    previewContainer.innerHTML = ''
  }

  await new Promise((resolve) => requestAnimationFrame(resolve))
}

/**
 * Initialize SDK (core method)
 * @param {Object} sdk - SDK instance
 * @param {string} productId - Product ID
 */
async function initializeSDK(sdk, productId) {
  try {
    const initConfig = {
      appId: SDS_CONFIG.appId,
      secret: SDS_CONFIG.sign,
      timestamp: Date.now(),
      productId: String(productId),
      previewContainerId: 'previewBox',
      designContainerId: 'designer-container',

      previewSize: getPreviewSize(),

      initCallback: () => {
        log.info('Designer Ready!')
        store.setStates({
          'design.isSDKReady': true,
          'design.loading': false,
        })

        eventBus.emit(EVENTS.SDK_READY)
        eventBus.emit(EVENTS.PRODUCT_CHANGE, productId)
      },

      designCallback: (data) => {
        if (data) {
          log.debug('Design callback:', data)
          eventBus.emit(EVENTS.CANVAS_UPDATE, data)
        }
      },
    }

    log.info('SDK init config:', { appId: initConfig.appId, productId: initConfig.productId })

    // Re-measure preview size if fallback was used
    const actualSize = getPreviewSize()
    if (actualSize === 290) {
      await new Promise((resolve) => requestAnimationFrame(resolve))
      initConfig.previewSize = getPreviewSize()
    } else {
      initConfig.previewSize = actualSize
    }

    sdk.init(initConfig)
    log.info('SDK init() called, waiting for callback...')

    // Timeout fallback: load variants even if initCallback never fires
    setTimeout(() => {
      if (!store.getState('design.isSDKReady')) {
        log.warn('10s passed without initCallback, loading variants anyway...')
        eventBus.emit(EVENTS.PRODUCT_CHANGE, productId)
      }
    }, 10000)
  } catch (error) {
    log.error('SDK init() threw error:', error)
    store.setState('design.loading', false)
    throw error
  }
}

export default {
  initDesigner,
  cleanup,
}
